import React from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { color } from '@/styles';
import { IResidentCareRecord } from '@/types/api/traversal/getNursingHomeCareRecord';
import { showMedicalContentSelector } from '@/redux/config/selfData';
import { ResidentRowHeader } from './residentRowHeader';

interface Props {
  idx: number;
  resident: IResidentCareRecord;
}

interface CareTiming {
  hour: number;
  minute: number;
}

interface RowData<T> {
  count: number;
  statusList: (T | null)[];
}

interface ResidentRowData {
  toilet: RowData<boolean>;
  pad: RowData<string>;
  defecation: RowData<string>;
}

const Th = css`
  border-bottom: 1px solid ${color.border_gray};
  border-right: 1px solid ${color.border_gray};
`;

const Td = css`
  text-align: center;
  border-bottom: 1px solid ${color.border_gray};
  border-right: 1px solid ${color.border_gray};
`;

const BgLightCell = (idx: number) => css`
  background-color: ${idx % 2 === 0 ? color.bg_light_green : color.white};
`;

const BgPaleCell = (idx: number) => css`
  background-color: ${idx % 2 === 0 ? color.bg_pale_green : color.white};
`;

const TableIcon = (width: number) => css`
  width: ${width}px;
  height: auto;
`;

const CareAlert = (careTimings: CareTiming[], index: number, showMedicalContent: boolean) => css`
  position: relative;

  > span:first-of-type {
    position: absolute;
    left: 0;
    top: 0;
    content: '';
    display: block;
    width: 50%;
    height: 100%;
    background-color: ${!showMedicalContent && careTimings.some((v) => v.hour === index && v.minute === 0)
      ? color.light_yellow
      : 'transparent'};
  }

  > span:nth-of-type(2) {
    position: absolute;
    left: 50%;
    top: 0;
    content: '';
    display: block;
    width: 50%;
    height: 100%;
    background-color: ${!showMedicalContent && careTimings.some((v) => v.hour === index && v.minute === 30)
      ? color.light_yellow
      : 'transparent'};
  }

  > img {
    position: relative;
  }
`;

export const ResidentRow = ({ idx, resident }: Props) => {
  const hourPerDay = 24;
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const [residentRowData, setResidentRowData] = React.useState<ResidentRowData>({
    toilet: { count: 0, statusList: [] },
    pad: { count: 0, statusList: [] },
    defecation: { count: 0, statusList: [] },
  });
  const [careTimings, setCareTimings] = React.useState<CareTiming[]>([]);
  const { t } = useTranslation();

  React.useEffect(() => {
    if (!resident) return;
    let data: ResidentRowData = {
      toilet: { count: 0, statusList: [] },
      pad: { count: 0, statusList: [] },
      defecation: { count: 0, statusList: [] },
    };

    // 同じ時間帯の中で複数の記録があった場合、有を優先して設定する（有が複数ある場合は最新の状態）
    const UpdateStatus = (hour: number, toilet: boolean | null, pad: string | null, defecation: string | null) => {
      // トイレ排尿
      if (data.toilet.statusList[hour] == null || toilet) {
        data.toilet.statusList[hour] = toilet;
      }
      // おむつ排尿
      if (data.pad.statusList[hour] == null || (pad !== 'none' && pad !== null)) {
        data.pad.statusList[hour] = pad;
      }
      // 排便
      if (data.defecation.statusList[hour] == null || (defecation !== 'none' && defecation !== null)) {
        data.defecation.statusList[hour] = defecation;
      }
    };

    for (let i = 0; i < hourPerDay; i++) {
      resident.allCareRecords
        .sort((a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf())
        .forEach((v) => {
          if (dayjs(v.timestamp).local().hour() === i) {
            if (v.urinatedToilet) {
              data.toilet.count++;
            }
            if (!!v.padUrineVolumeType && v.padUrineVolumeType !== 'none') {
              data.pad.count++;
            }
            if (!!v.defecationVolumeType && v.defecationVolumeType !== 'none') {
              data.defecation.count++;
            }

            UpdateStatus(i, v.urinatedToilet, v.padUrineVolumeType, v.defecationVolumeType);
          }
        });
    }
    setResidentRowData(data);

    const timings = resident.careTimings.map((v) => {
      const timing = dayjs().utc().set('hour', +v.slice(0, 2)).set('minute', +v.slice(3, 5)).local();
      return { hour: timing.hour(), minute: timing.minute() };
    });

    setCareTimings(timings);
  }, [resident]);

  const ToiletCellIcon = (value: boolean | null) => {
    if (typeof value === 'boolean') {
      if (value) {
        return (
          <img
            css={TableIcon(16)}
            src='/images/excretionTable/urine_toilet.svg'
            alt={t('HasToiletUrination', 'トイレ排尿 有')}
          />
        );
      } else {
        return (
          <img
            css={TableIcon(16)}
            src='/images/excretionTable/urine_toilet_none.svg'
            alt={t('NoToiletUrination', 'トイレ排尿 無')}
          />
        );
      }
    }
    return <></>;
  };

  const PadCellIcon = (value: string | null) => {
    if (typeof value === 'string') {
      if (value === 'none') {
        return (
          <img
            css={TableIcon(14)}
            src='/images/excretionTable/urine_pad_none.svg'
            alt={t('NoDiaperUrination', 'オムツ・パッド排尿 無')}
          />
        );
      } else if (value === 'small') {
        return (
          <img
            css={TableIcon(9)}
            src='/images/excretionTable/urine_pad_small.svg'
            alt={t('HasSmallDiaperUrination', 'オムツ・パッド排尿 小')}
          />
        );
      } else if (value === 'medium') {
        return (
          <img
            css={TableIcon(21)}
            src='/images/excretionTable/urine_pad_medium.svg'
            alt={t('HasMiddleDiaperUrination', 'オムツ・パッド排尿 中')}
          />
        );
      } else if (value === 'large') {
        return (
          <img
            css={TableIcon(21)}
            src='/images/excretionTable/urine_pad_large.svg'
            alt={t('HasLargeDiaperUrination', 'オムツ・パッド排尿 多')}
          />
        );
      }
    }
    return <></>;
  };

  const DefecationCellIcon = (value: string | null) => {
    if (typeof value === 'string') {
      if (value === 'none') {
        return <img css={TableIcon(19)} src='/images/excretionTable/defecation_none.svg' alt='排便 無' />;
      } else {
        return <img css={TableIcon(19)} src='/images/excretionTable/defecation.svg' alt='排便 有' />;
      }
    }
    return <></>;
  };

  return (
    <>
      <tr>
        <th rowSpan={3} css={[Th, BgLightCell(idx)]}>
          <ResidentRowHeader
            id={resident.residentId}
            name={resident.residentName}
            unitName={resident.nursingHomeUnitName}
            roomName={resident.roomName}
          />
        </th>
        <td css={[Td, BgLightCell(idx)]}>
          <img css={TableIcon(22)} src='/images/excretionTable/toilet.svg' alt={t('toilet', 'トイレ')} />
        </td>
        <td css={[Td, BgLightCell(idx)]}>{residentRowData.toilet.count}</td>
        {Array.from({ length: hourPerDay }, (_, i) => (
          <td key={i} css={[Td, BgPaleCell(idx), CareAlert(careTimings, i, showMedicalContent)]}>
            <span></span>
            <span></span>
            {ToiletCellIcon(residentRowData.toilet.statusList[i])}
          </td>
        ))}
      </tr>
      <tr>
        <td css={[Td, BgLightCell(idx)]}>
          <img css={TableIcon(23)} src='/images/excretionTable/diaper.svg' alt={t('diaperOrPad', 'おむつ/パッド')} />
        </td>
        <td css={[Td, BgLightCell(idx)]}>{residentRowData.pad.count}</td>
        {Array.from({ length: hourPerDay }, (_, i) => (
          <td key={i} css={[Td, BgPaleCell(idx), CareAlert(careTimings, i, showMedicalContent)]}>
            <span></span>
            <span></span>
            {PadCellIcon(residentRowData.pad.statusList[i])}
          </td>
        ))}
      </tr>
      <tr>
        <td css={[Td, BgLightCell(idx)]}>
          <img css={TableIcon(19)} src='/images/excretionTable/defecation.svg' alt={t('便', '便')} />
        </td>
        <td css={[Td, BgLightCell(idx)]}>{residentRowData.defecation.count}</td>
        {Array.from({ length: hourPerDay }, (_, i) => (
          <td key={i} css={[Td, BgPaleCell(idx), CareAlert(careTimings, i, showMedicalContent)]}>
            <span></span>
            <span></span>
            {DefecationCellIcon(residentRowData.defecation.statusList[i])}
          </td>
        ))}
      </tr>
    </>
  );
};
